import Candidate, { ErrorCandidate } from 'requests/objects/candidate'
import ApiHandler from 'requests/apiHandler'
import FileCv from 'requests/objects/fileCv'

/**
 * CandidatesHandler
 * @augments {ApiHandler<Candidate, ErrorCandidate>}
 */
export default class CandidatesHandler extends ApiHandler {
    constructor() {
        super({ type: Candidate, errorType: ErrorCandidate, key: 'candidates' })
    }

    /**
     * Get file
     * @param {number} candidateId Id
     * @param {number} fileId Id
     * @returns {import('requests/apiHandler').RequestApi<Blob>} Request
     */
    getFileCv(candidateId, fileId) {
        const request = this.initFetchRequest({
            url: [candidateId, 'filecvs', fileId], method: 'GET', responseType: 'arraybuffer',
        })

        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(res => new Blob([res.data]))
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Upload file
     * @param {number} candidateId Id
     * @param {globalThis.File} file file
     * @returns {import('requests/apiHandler').RequestApi<FileCv>} Returns
     */
    uploadFileCv(candidateId = undefined, file = undefined) {
        const formData = new FormData()
        formData.append('file', file)

        const request = this.initFetchRequest({
            url: [candidateId, 'filecvs'],
            method: 'POST',
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data;',
            },
        })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => new FileCv(/** @type {any} */(res.data)?.filecvs))
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Remove file
     * @param {number} candidateId Id
     * @param {number} fieldId Ids
     * @returns {import('requests/apiHandler').RequestApi<FileCv>} Request
     */
    removeFileCv(candidateId, fieldId) {
        const request = this.initFetchRequest({
            url: [candidateId, 'filecvs', fieldId], method: 'DELETE',
        })

        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(() => [])
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Import parameters from a candidacy
     * @param {number} candidateId Id
     * @param {number} candidacyId Id
     * @returns {import('requests/apiHandler').RequestApi<Candidate>} Request
     */
    codingCandidacy(candidateId, candidacyId) {
        const request = this.initFetchRequest({ url: [candidateId, 'coding', candidacyId], method: 'PATCH' })

        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(res => new (this.type)(res.data[this.objectName]))
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Delete candidate by id
     * @param {number} candidateId Id
     * @returns {import('requests/apiHandler').RequestApi<any>} Request
     */
    deleteById(candidateId) {
        const request = this.initFetchRequest({
            url: [candidateId],
            method: 'DELETE',
        })
        return this.getRequestApi(
            () => request.fetchRequest.then(res => res.data),
            request.cancelToken,
        )
    }

    /**
     * Get candidate duplicates
     * @returns {import('requests/apiHandler').RequestApi<Candidate[][]>} Request
     */
    getDuplicates() {
        const request = this.initFetchRequest({ url: ['duplicates'], method: 'GET' })
        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(res => /** @type {any} */(res.data[this.objectName])?.map(x => x?.map(y => new (this.type)(y))) ?? [])
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Merge candidate duplicates
     * @param {{candidateId: number, isKept: boolean, replacedByCandidateId?: number}[]} candidates Array of candidates to merge
     * @returns {import('requests/apiHandler').RequestApi<any>} Request
     */
    mergeDuplicates(candidates) {
        const request = this.initFetchRequest({
            url: ['duplicates', 'merge'],
            method: 'POST',
            data: { candidates },
        })
        return this.getRequestApi(
            () => request.fetchRequest.then(res => res.data),
            request.cancelToken,
        )
    }
}
